import { useEffect, useState } from "react"
import Footer from "../components/footer"
import Menu from "../components/menu"
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemButton,
} from "react-accessible-accordion"
import { NextSeo } from "next-seo"
import Link from "next/link"

function Calculator() {
  const [price, setPrice] = useState(2.5e5)

  const [oneTimeCosts, setOneTimeCosts] = useState(0)

  const [rent, setRent] = useState(900)

  const [ongoingCostsMonthly, setOngoingCostsMonthly] = useState(0)
  const [ongoingCostsYearly, setOngoingCostsYearly] = useState(0)

  useEffect(() => {
    const max = 0.5 * rent
    if (ongoingCostsMonthly > max) {
      setOngoingCostsMonthly(max)
    }
  }, [rent])

  useEffect(() => {
    const max = 12 * rent * 0.33
    if (ongoingCostsYearly > max) {
      setOngoingCostsYearly(max)
    }
  }, [rent])

  const [rotate1, setRotate1] = useState(false)
  const [rotate2, setRotate2] = useState(false)

  const annualRent = rent * 12
  const annualIncome = annualRent - ongoingCostsYearly - ongoingCostsMonthly * 12

  const grossYield = annualRent / (price + oneTimeCosts)
  const gross = grossYield < 0 ? grossYield * 100 : grossYield * 100

  const netYield = annualIncome / (price + oneTimeCosts)

  const net =
    ongoingCostsMonthly === 0 && ongoingCostsYearly === 0 && oneTimeCosts === 0
      ? gross
      : netYield < 0
      ? netYield * 1000
      : netYield * 100

  return (
    <>
      <NextSeo
        title="Rental Yield Calculator"
        description="Discover your potential rental income with the Huntleys Rental Yield Calculator. Easily estimate your rental yield, both gross and net with costs considered."
      />
      <div className="flex flex-col items-center justify-center h-full min-h-screen">
        <div className="absolute top-0 bottom-0 left-0 right-0">
          <img className="object-cover w-full h-full select-none" src="/images/links.webp" />
          <div className="absolute top-0 bottom-0 left-0 right-0 items-center justify-center w-full h-full pt-10 overflow-y-scroll no-scrollbar bg-opacity-30 bg-stone-900 backdrop-blur sm:pt-24">
            <div className="justify-center w-full mx-auto">
              <div className="flex h-full px-4">
                <div className="z-10 flex flex-col flex-1 w-full h-full max-w-4xl gap-4 pb-12 mx-auto overflow-hidden">
                  <div className="p-8 rounded bg-stone-900 bg-opacity-90">
                    <div className="pb-4 border-b border-stone-600">
                      <h1 className="font-serif text-2xl font-semibold text-white">Rental Yield Calculator</h1>
                    </div>
                    <div className="mt-6">
                      <div className="flex justify-between text-white">
                        <div>
                          <p>Purchase Price</p>
                          <p className="text-lg font-medium">
                            {new Intl.NumberFormat("en-gb", {
                              style: "currency",
                              currency: "GBP",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(price)}
                          </p>
                        </div>
                      </div>
                      <input
                        aria-label="Property Purchase Price for Rental Yield Calculator"
                        type="range"
                        min={1e5}
                        max={6e5}
                        className="w-full h-2 mt-3 text-white rounded-full appearance-none accent-cpink-200 bg-cpink-50"
                        value={price}
                        onChange={e => setPrice(Math.round(Number.parseInt(e.target.value) / 1000) * 1000)}
                      />
                      <Accordion
                        className="flex flex-col items-start justify-center flex-1 w-full lg:flex-row gap-x-8"
                        allowZeroExpanded
                      >
                        <div className="flex flex-col items-center justify-center flex-1 w-full h-full text-white">
                          <AccordionItem className="w-full h-full">
                            <AccordionItemHeading className="mt-3" onClick={() => setRotate1(rotate1 => !rotate1)}>
                              <AccordionItemButton className="flex items-center text-sm font-medium gap-x-1">
                                <div>
                                  <div className={`${rotate1 ? "rotate-180" : "rotate-0"} transition-all duration-500`}>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      className="w-4 h-4"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M12.53 16.28a.75.75 0 01-1.06 0l-7.5-7.5a.75.75 0 011.06-1.06L12 14.69l6.97-6.97a.75.75 0 111.06 1.06l-7.5 7.5z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                <p>One-time purchase costs</p>
                              </AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel className="mt-2 text-xs transition-all duration-300">
                              <p className="text-stone-400">
                                One-time purchase costs can include, but aren't limited to, stamp duty, legal fees, surveyor
                                fees, mortgage fees, insurance costs, property valuation and Land Registry fees.
                              </p>
                              <div className="flex items-center justify-between gap-6 mt-4">
                                <div className="">
                                  <p className="text-sm text-stone-200">One-time purchase costs</p>
                                  <p className="text-base font-medium">
                                    {new Intl.NumberFormat("en-gb", {
                                      style: "currency",
                                      currency: "GBP",
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    }).format(oneTimeCosts)}
                                  </p>
                                </div>
                                <input
                                  onChange={e => setOneTimeCosts(Math.round(Number.parseInt(e.target.value) / 100) * 100)}
                                  aria-label="Monthly Rent for Rental Yield Calculator"
                                  type="range"
                                  min={0}
                                  max={30e3}
                                  className="flex-1 h-2 max-w-xs text-white rounded-full appearance-none accent-cpink-200 bg-cpink-50"
                                  value={oneTimeCosts}
                                />
                              </div>
                            </AccordionItemPanel>
                          </AccordionItem>
                        </div>
                      </Accordion>
                    </div>
                    <div className="mt-8">
                      <div className="flex justify-between text-white">
                        <div>
                          <p className="text-stone-200">Monthly Rent</p>
                          <p className="text-lg font-medium">
                            {new Intl.NumberFormat("en-gb", {
                              style: "currency",
                              currency: "GBP",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(rent)}
                          </p>
                        </div>
                      </div>
                      <input
                        aria-label="Monthly Rent for Rental Yield Calculator"
                        type="range"
                        onChange={e => setRent(Math.round(Number.parseInt(e.target.value) / 10) * 10)}
                        min={500}
                        max={2500}
                        className="w-full h-2 mt-3 text-white rounded-full appearance-none accent-cpink-200 bg-cpink-50"
                        value={rent}
                      />
                      <Accordion
                        className="flex flex-col items-start justify-center flex-1 w-full lg:flex-row gap-x-8"
                        allowZeroExpanded
                      >
                        <div className="flex flex-col items-center justify-center flex-1 w-full h-full text-white">
                          <AccordionItem className="w-full h-full">
                            <AccordionItemHeading className="mt-3" onClick={() => setRotate2(rotate2 => !rotate2)}>
                              <AccordionItemButton className="flex items-center text-sm font-medium gap-x-1">
                                <div>
                                  <div className={`${rotate2 ? "rotate-180" : "rotate-0"} transition-all duration-500`}>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      className="w-4 h-4"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M12.53 16.28a.75.75 0 01-1.06 0l-7.5-7.5a.75.75 0 011.06-1.06L12 14.69l6.97-6.97a.75.75 0 111.06 1.06l-7.5 7.5z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                <p>Ongoing costs</p>
                              </AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel className="mt-2 text-xs transition-all duration-300">
                              <p className="text-stone-400">
                                Ongoing costs can include, but aren't limited to, property maintenance, taxes, insurance,
                                utilities, and management fees.
                              </p>
                              <div className="flex items-center justify-between gap-6 mt-4">
                                <div className="">
                                  <p className="text-sm text-stone-200">Monthly Ongoing costs</p>
                                  <p className="text-base font-medium">
                                    {new Intl.NumberFormat("en-gb", {
                                      style: "currency",
                                      currency: "GBP",
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    }).format(ongoingCostsMonthly)}
                                  </p>
                                </div>
                                <input
                                  aria-label="Monthly Rent for Rental Yield Calculator"
                                  type="range"
                                  onChange={e => setOngoingCostsMonthly(Math.round(Number.parseInt(e.target.value) / 10) * 10)}
                                  min={0}
                                  max={0.5 * rent}
                                  className="flex-1 h-2 max-w-xs text-white rounded-full appearance-none accent-cpink-200 bg-cpink-50"
                                  value={ongoingCostsMonthly}
                                />
                              </div>
                              <div className="flex items-center justify-between gap-6 mt-4">
                                <div className="">
                                  <p className="text-sm text-stone-200">Yearly Ongoing costs</p>
                                  <p className="text-base font-medium">
                                    {new Intl.NumberFormat("en-gb", {
                                      style: "currency",
                                      currency: "GBP",
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    }).format(ongoingCostsYearly)}
                                  </p>
                                </div>
                                <input
                                  aria-label="Yearly Ongoing COsts for Rental Yield Calculator"
                                  type="range"
                                  onChange={e => setOngoingCostsYearly(Math.round(Number.parseInt(e.target.value) / 10) * 10)}
                                  min={0}
                                  max={12 * rent * 0.33}
                                  className="flex-1 h-2 max-w-xs text-white rounded-full appearance-none accent-cpink-200 bg-cpink-50"
                                  value={ongoingCostsYearly}
                                />
                              </div>
                            </AccordionItemPanel>
                          </AccordionItem>
                        </div>
                      </Accordion>
                    </div>
                    <div className="flex pt-6 mt-6 border-t border-stone-600">
                      <div className="flex justify-center flex-1 text-center text-white">
                        <div>
                          <p className="font-serif text-4xl font-medium">{gross.toFixed(2)}%</p>
                          <p className="mt-1 text-stone-300">Gross Yield</p>
                        </div>
                      </div>
                      <div className="flex justify-center flex-1 text-center text-white">
                        <div>
                          <p className="font-serif text-4xl font-medium">{net.toFixed(2)}%</p>
                          <p className="mt-1 text-stone-300">Net Yield</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <div className="flex justify-between w-full p-4 rounded bg-stone-900 bg-opacity-90">
                  <div className="flex justify-center flex-1 text-center text-white">
                    <div>
                      <p className="font-serif text-4xl font-medium">{gross.toFixed(2)}%</p>
                      <p>Gross Yield</p>
                    </div>
                  </div>
                  <div className="w-px bg-white"></div>
                  <div className="flex justify-center flex-1 text-center text-white">
                    <div>
                      <p className="font-serif text-4xl font-medium">{net.toFixed(2)}%</p>
                      <p>Net Yield</p>
                    </div>
                  </div>
                </div> */}
                </div>
              </div>
              <div className="px-4">
                <div className="z-10 flex flex-col w-full max-w-xl gap-4 mx-auto mb-12 overflow-hidden lg:max-w-4xl">
                  <div className="p-8 text-white rounded bg-stone-900 bg-opacity-90">
                    <div className="pb-4 border-b border-cpink">
                      <h1 className="font-serif text-2xl font-semibold text-white">Additional information</h1>
                    </div>
                    <h2 className="mt-6 text-lg font-medium">What is Rental Yield?</h2>
                    <p className="mt-2 text-sm text-stone-400">
                      Rental yield is the annual rental income expressed as a percentage of the property's value. It is a key
                      metric in comparing the rental returns of different investment properties and part of calculating return
                      on investment (ROI). Our rental yield calculator lets you input your estimated values and calculates both
                      the gross and net yields. It should not be considered investment advice nor be used to calculate rent.
                    </p>
                    <h2 className="mt-6 text-lg font-medium">Understanding Gross vs. Net Rental Yield</h2>
                    <p className="mt-2 text-sm text-stone-400">
                      Gross rental yield is the percentage of yield before expenses. This is what most estate agents use when
                      advertising properties. Net rental yield considers purchasing costs and ongoing expenses like insurance,
                      maintenance, and management fees. As a result, net rental yield provides a more comprehensive and
                      realistic view of the property's value as an investment.
                    </p>
                    <h2 className="mt-6 text-lg font-medium">What makes a good yield?</h2>
                    <p className="mt-2 text-sm text-stone-400">
                      What constitutes a good rental yield percentage depends largely on the property's risk profile. This
                      typically includes location risks, other local development, prevailing market conditions, shifting demand,
                      and your own goals. The higher the risk, the better the return needs to be to compensate. Additionally,
                      consider long-term capital appreciation potential, the wider economic environment, and the amount of work
                      a property will be when considering the rental yield's suitability. It is a more practical tool for
                      comparing options as opposed to trying to calculate real-world returns.
                    </p>
                    <p className="mt-2 text-sm text-stone-400">
                      A gross rental yield of around 5% or higher for conventional residential properties is considered good for
                      well-established and stable markets like Loughborough. For higher risk/management-intensive options like
                      student properties, a return closer to 10% is considered good.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="z-10 px-4 pb-10">
              <div className="relative flex">
                <div className="block w-full mx-auto sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
                  <div className="flex flex-col flex-wrap items-center gap-8 text-white md:grid-cols-2 xl:grid-cols-3 md:grid xl:flex-row lg:gap-12">
                    <div className="flex-1 w-full h-full overflow-hidden translate-x-0 bg-white rounded drop-shadow-sm">
                      <div className="p-4 bg-teal-900">
                        <h1 className="text-lg font-medium ">Find a tenant</h1>
                        <div className="flex items-center">
                          <p className="text-xl font-medium text-white">£</p>
                          <h1 className="ml-1 text-5xl font-medium">450</h1>
                          <p className="ml-2 font-medium text-cpink">inc. VAT</p>
                        </div>
                      </div>
                      <div className="flex flex-col p-4 bg-white gap-y-2">
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Full marketing campaign inc Floorplans</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Video tours</p>
                        </div>

                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Prequalification of applicants</p>
                        </div>

                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Conduct viewings and provide feedback</p>
                        </div>

                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Negotiate the best deal for you</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Provide you with a referencing report</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Produce a comprehensive tenancy agreement</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Set up a standing order</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Secure funds and handover keys</p>
                        </div>
                      </div>
                      <div className="flex flex-col justify-end p-4 border-t border-stone-200">
                        <Link href="/valuation?type=let">
                          <button
                            aria-label="Select"
                            className="w-full px-4 py-3 font-medium transition-all duration-300 bg-teal-800 border-2 border-teal-900 rounded select-none text-teal-50 hover:bg-teal-900"
                          >
                            Select
                          </button>
                        </Link>
                      </div>
                    </div>
                    <div className="flex-1 w-full overflow-hidden translate-x-0 bg-white rounded drop-shadow-sm">
                      <div className="p-4 bg-teal-900">
                        <h1 className="text-lg font-medium ">Rent collection</h1>
                        <div className="flex items-center">
                          <p className="text-xl font-medium text-white">£</p>
                          <h1 className="ml-1 text-5xl font-medium">300</h1>
                          <p className="ml-2 text-xl font-medium text-cpink">+</p>
                          <h1 className="ml-2 text-5xl font-medium">6%</h1>
                          <p className="ml-2 font-medium text-cpink">inc. VAT</p>
                        </div>
                      </div>
                      <div className="flex flex-col p-4 bg-white gap-y-2">
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Our tenant find service +</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Register the Tenant’s deposit</p>
                        </div>

                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Collect and process all rent payments</p>
                        </div>

                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Contact the tenant regarding any rent arrears</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Advise on action when a tenant doesn't pay</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Produce and email monthly statements</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Produce annual summaries of account</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Negotiate any renewal or extension</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Serve any required notices</p>
                        </div>
                      </div>
                      <div className="p-4 bg-white border-t border-stone-200">
                        <Link href="/valuation?type=let">
                          <button
                            aria-label="Select"
                            className="w-full px-4 py-3 font-medium transition-all duration-300 bg-teal-800 border-2 border-teal-900 rounded select-none text-teal-50 hover:bg-teal-900"
                          >
                            Select
                          </button>
                        </Link>
                      </div>
                    </div>
                    <div className="flex-1 w-full h-full overflow-hidden translate-x-0 bg-white rounded drop-shadow-sm">
                      <div className="p-4 bg-teal-900">
                        <h1 className="text-lg font-medium ">Fully Managed</h1>
                        <div className="flex items-center">
                          <p className="text-xl font-medium text-white">£</p>
                          <h1 className="ml-1 text-5xl font-medium">300</h1>
                          <p className="ml-2 text-xl font-medium text-cpink">+</p>
                          <h1 className="ml-2 text-5xl font-medium">9%</h1>
                          <p className="ml-2 font-medium text-cpink">inc. VAT</p>
                        </div>
                      </div>
                      <div className="flex flex-col p-4 bg-white gap-y-2">
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Our rent collection service +</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Produce an Inventory & Condition Report</p>
                        </div>

                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Record the Council tax and Utility liability</p>
                        </div>

                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Quarterly property visits</p>
                        </div>

                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Manage maintenance Arranges (no mark-up)</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Act as the tenant's point of contact</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Manage any statutory safety checks</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Conduct a checkout inspection</p>
                        </div>
                        <div className="flex items-center gap-x-2 text-stone-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-teal-900"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <p className="font-medium">Negotiate any deposit deductions</p>
                        </div>
                      </div>
                      <div className="p-4 bg-white border-t border-stone-200">
                        <Link href="/valuation?type=let">
                          <button
                            aria-label="Select"
                            className="w-full px-4 py-3 font-medium transition-all duration-300 bg-teal-800 border-2 border-teal-900 rounded select-none text-teal-50 hover:bg-teal-900"
                          >
                            Select
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* <div className="flex justify-center w-full px-4 py-12 bg-cpink">
              <div className="w-full mx-auto sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
                <h1 className="w-full font-serif text-4xl font-semibold bg-cpink text-stone-900 sm:text-left">
                  Looking to let your property? <br className="hidden sm:block" />
                  Contact us to learn more.
                </h1>
                <Link href="/valuation?type=let" prefetch={false}>
                  <button
                    aria-label="Arrange a valuation"
                    className="px-4 py-3 mt-6 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 hover:bg-white border-cpink-200 bg-cpink-200 hover:border-white"
                  >
                    Arrange a valuation
                  </button>
                </Link>
              </div>
            </div> */}
            <section>
              <div className="relative flex bg-white">
                <div className="justify-center hidden w-full mx-auto max-h-64 sm:flex sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
                  <div className="absolute bottom-0 left-0 right-0 flex top-12 bg-cpink"></div>
                  <div className="z-10 items-center justify-center w-1/4 px-4 -mt-48 sm:-mt-0 sm:-mb-32 sm:w-2/5 md:-mb-48 lg:w-1/6 xl:w-2/7">
                    <img alt="Phone" className="w-full select-none" src="/images/phone-3.png" />
                    <img alt="Laptop" className="w-full select-none sm:hidden" src="/images/phone-3.png" />
                  </div>
                  <div className="z-10 flex w-3/5 pt-12 md:py-12 md:pt-[4.75rem]">
                    <div className="px-4 my-auto">
                      <h2 className="w-full font-serif text-4xl font-semibold text-stone-900 sm:text-left drop-shadow">
                        Find out what your property is worth
                      </h2>
                      <Link href="/valuation?type=let" target="_blank">
                        <button className="px-4 py-3 mt-6 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 bg-cpink-200 border-cpink-200 hover:bg-white hover:border-white">
                          Arrange a valuation
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white sm:hidden">
                <div className="flex justify-center px-4 sm:hidden">
                  <img alt="Laptop" className="w-full max-w-sm select-none" src="/images/phone-3.png" />
                </div>
                <div className="relative px-4 -mt-96 bg-cpink">
                  <div className="flex py-12 ">
                    <div className="flex-1 min-w-0 sm:flex-2">
                      <h2 className="w-full font-serif text-4xl font-semibold text-stone-900 sm:text-left drop-shadow">
                        Find out what your property is worth
                      </h2>
                      <Link href="/valuation?type=let" target="_blank">
                        <button className="px-4 py-3 mt-6 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 bg-cpink-200 border-cpink-200 hover:bg-white hover:border-white">
                          Arrange a Valuation
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <Footer />
            </section>
          </div>
        </div>
      </div>
      <Menu />
    </>
  )
}

export default Calculator

